import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, BellRing, DoorOpen, CheckCircle2, Clock, Home, CircleDot,
  Wrench, TrendingUp, Wallet, PiggyBank, Flame, ShieldCheck, Activity, ArrowRight,
  BedDouble, CalendarCheck, Cake, LogOut,
} from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import { PageHeader, SectionCard, StatCard, Money, Loading, EmptyState } from "@/components/Common";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { formatTanggal, formatTanggalWaktu, formatRupiah, APPROVAL_TYPE } from "@/lib/format";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const SEV = {
  danger: { bg: "#fef2f2", border: "#fecaca", text: "#b91c1c", Icon: Flame },
  warning: { bg: "#fff7ed", border: "#ffedd5", text: "#c2410c", Icon: BellRing },
  info: { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8", Icon: Clock },
};

const SERIES = { investor: "Bagian Investor", care: "Dana Perawatan", ipl: "IPL" };
const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const monthLabel = (m) => { const [y, mo] = String(m).split("-"); return `${BULAN[Number(mo) - 1] || mo} ${String(y).slice(2)}`; };
const compactRp = (v) => (v >= 1e6 ? `${(v / 1e6).toFixed(1)}jt` : v >= 1e3 ? `${Math.round(v / 1e3)}rb` : `${v}`);
const OCC_COLORS = ["#dd6030", "#0369a1", "#7e22ce", "#047857", "#c2410c", "#0891b2"];

function ReminderCard({ r, onGo }) {
  const sev = SEV[r.severity] || SEV.info;
  return (
    <div className="rounded-lg border p-4" style={{ backgroundColor: sev.bg, borderColor: sev.border }} data-testid="reminder-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <sev.Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color: sev.text }} />
          <div>
            <div className="text-sm font-semibold" style={{ color: sev.text }}>{r.title}</div>
            <div className="mt-0.5 text-sm font-medium text-foreground">{r.resident_name}</div>
            <div className="text-xs text-muted-foreground">
              {r.project_name} · {r.house_name} · Kamar {r.room_number}
            </div>
          </div>
        </div>
        <div className="text-right">
          {r.lease_end && <div className="text-xs text-muted-foreground">Akhir sewa</div>}
          {r.lease_end && <div className="text-sm font-semibold">{r.lease_end}</div>}
          {r.deadline && <div className="text-xs text-muted-foreground">Batas</div>}
          {r.deadline && <div className="text-sm font-semibold">{formatTanggalWaktu(r.deadline)}</div>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-full bg-white/70 px-2 py-0.5 font-medium text-foreground">{r.extension_status}</span>
        <span className="rounded-full bg-white/70 px-2 py-0.5 font-medium text-foreground">{r.payment_status}</span>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{r.action}</span>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={onGo} data-testid="reminder-action">
          Tindak lanjut <ArrowRight className="ml-1 h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function GhCheckoutTomorrow({ items, onGo }) {
  return (
    <SectionCard
      title="Check-out Besok — Guest House"
      description="Tamu harian & mingguan yang check-out besok. Siapkan kamar setelah tamu keluar."
      testid="section-gh-checkout-tomorrow"
      actions={<Button variant="outline" size="sm" onClick={onGo} data-testid="gh-checkout-tomorrow-go">Buka Guest House <ArrowRight className="ml-1 h-3 w-3" /></Button>}
    >
      {items.length === 0 ? (
        <EmptyState title="Tidak ada check-out besok" description="Belum ada tamu harian atau mingguan yang keluar besok." icon={CheckCircle2} />
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="gh-checkout-tomorrow-list">
          {items.map((s) => (
            <div key={s.id} data-testid={`gh-checkout-tomorrow-${s.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                <LogOut className="h-4 w-4 shrink-0 text-sky-700" />
                <div className="min-w-0">
                  <div className="truncate font-medium">{s.guest_name}</div>
                  <div className="truncate text-xs text-muted-foreground">{s.building_name} · Kamar {s.room_number}{s.whatsapp ? ` · ${s.whatsapp}` : ""}</div>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.kind === "weekly" ? "bg-amber-100 text-amber-800" : "bg-orange-100 text-orange-800"}`}>{s.kind_label}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">Keluar {formatTanggal(s.check_out)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

export default function Dashboard() {
  const { isSuperadmin } = useAuth();
  const { projectId, projectParam } = useProjects();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", projectId],
    queryFn: async () => (await api.get("/dashboard", { params: projectParam })).data,
  });

  const { data: myApprovals } = useQuery({
    queryKey: ["my-approvals", projectId],
    queryFn: async () => (await api.get("/approvals", { params: { status: "pending" } })).data,
    enabled: !isSuperadmin,
  });

  const { data: trend = [] } = useQuery({
    queryKey: ["finance-trend", projectId],
    queryFn: async () => (await api.get("/finance/trend", { params: { months: 6 } })).data,
  });

  const { data: occ } = useQuery({
    queryKey: ["occupancy", projectId],
    queryFn: async () => (await api.get("/occupancy", { params: { ...projectParam, months: 6 } })).data,
  });

  const reconcile = useMutation({
    mutationFn: async () => (await api.post("/reconcile")).data,
    onSuccess: (res) => {
      toast.success("Rekonsiliasi selesai", {
        description: `Booking kedaluwarsa: ${res.bookings_expired}, sewa ditutup: ${res.leases_ended}, reminder: ${res.reminders_created}.`,
      });
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (isLoading) return <Loading />;
  const rs = data?.room_summary || { counts: {}, being_prepared: 0, total: 0 };
  const fin = data?.finance || {};

  return (
    <div className="space-y-6 md:space-y-8">
      <PageHeader
        title="Dashboard"
        description="Ringkasan operasional dan keuangan kos."
        actions={
          <Button onClick={() => reconcile.mutate()} disabled={reconcile.isPending} variant="outline" data-testid="reconcile-button">
            <RefreshCw className={`mr-2 h-4 w-4 ${reconcile.isPending ? "animate-spin" : ""}`} />
            Jalankan Rekonsiliasi
          </Button>
        }
      />

      {/* Reminders */}
      <SectionCard
        title="Pengingat & Pekerjaan Mendesak"
        description="Reminder H-14 & H-4 sebelum akhir sewa serta booking mendekati batas 96 jam."
        testid="section-reminders"
      >
        {data?.reminders?.length ? (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {data.reminders.map((r, i) => (
              <ReminderCard key={i} r={r} onGo={() => navigate("/booking-sewa")} />
            ))}
          </div>
        ) : (
          <EmptyState title="Tidak ada pengingat" description="Semua sewa dan booking dalam kondisi aman." icon={CheckCircle2} />
        )}
      </SectionCard>

      {/* Birthdays */}
      {data?.birthdays?.length > 0 && (
        <SectionCard title="Ulang Tahun Penghuni" description="Penghuni yang masih menyewa dan berulang tahun dalam 14 hari ke depan." testid="section-birthdays">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {data.birthdays.map((b) => (
              <div key={b.id} data-testid={`birthday-${b.id}`} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm" style={{ backgroundColor: "#fdf2f8", borderColor: "#fbcfe8" }}>
                <div className="flex items-center gap-2">
                  <Cake className="h-4 w-4 shrink-0" style={{ color: "#be185d" }} />
                  <div>
                    <div className="font-medium">{b.name}</div>
                    <div className="text-xs text-muted-foreground">{b.project_name}{b.whatsapp ? ` · ${b.whatsapp}` : ""}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-semibold" style={{ color: "#be185d" }}>{b.days_until === 0 ? "Hari ini" : `${b.days_until} hari lagi`}</div>
                  <div className="text-[11px] text-muted-foreground">{b.date_label} · {b.age_turning} th</div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <GhCheckoutTomorrow items={data?.gh_checkouts_tomorrow || []} onGo={() => navigate("/guest-house")} />

      {/* Approvals */}
      {isSuperadmin ? (
        <SectionCard testid="section-pending-approvals" className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-semibold">Menunggu Keputusan Anda</div>
              <div className="text-xs text-muted-foreground">
                {data?.pending_approvals || 0} pengajuan keuangan · {data?.pending_exit_conflicts || 0} konflik keluar lebih awal.
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate("/persetujuan")} data-testid="go-approvals">
            Buka Persetujuan
          </Button>
        </SectionCard>
      ) : (
        <SectionCard title="Pengajuan Saya" description="Status pengajuan perubahan keuangan Anda." testid="section-my-approvals">
          {myApprovals?.length ? (
            <div className="space-y-2">
              {myApprovals.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <div className="text-sm">
                    <span className="font-medium">{APPROVAL_TYPE[a.type] || a.type}</span>
                    <span className="text-muted-foreground"> · {a.reason}</span>
                  </div>
                  <StatusBadge kind="approval" value={a.status} />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="Tidak ada pengajuan menunggu" icon={ShieldCheck} />
          )}
        </SectionCard>
      )}

      {isSuperadmin && data?.summary_status?.schedule_enabled && !data?.summary_status?.sent && (
        <div className="flex items-center justify-between gap-3 rounded-lg border p-4" style={{ backgroundColor: "#fff7ed", borderColor: "#ffedd5" }} data-testid="summary-unsent-badge">
          <div className="flex items-center gap-3">
            <BellRing className="h-5 w-5 shrink-0" style={{ color: "#c2410c" }} />
            <div>
              <div className="text-sm font-semibold" style={{ color: "#c2410c" }}>Ringkasan pencairan belum dikirim</div>
              <div className="text-xs text-muted-foreground">Ringkasan investor periode {data.summary_status.period} belum terkirim bulan ini.</div>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate("/pencairan-investor")} data-testid="summary-unsent-go">Kirim Sekarang</Button>
        </div>
      )}

      {isSuperadmin && data?.top_outstanding?.length > 0 && (
        <SectionCard title="Jatuh Tempo Pencairan Investor" description="Investor dengan sisa belum dicairkan terbesar (tahun berjalan)." testid="section-top-outstanding">
          <div className="space-y-2">
            {data.top_outstanding.map((r) => (
              <div key={r.investor_id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid={`top-outstanding-${r.investor_id}`}>
                <div className="flex items-center gap-2 text-sm"><PiggyBank className="h-4 w-4 text-primary" /><span className="font-medium">{r.investor_name}</span></div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-primary"><Money value={r.outstanding} /></span>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => navigate("/pencairan-investor")} data-testid={`top-outstanding-go-${r.investor_id}`}>Catat <ArrowRight className="ml-1 h-3 w-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Room summary */}
      <div>
        <h2 className="mb-3 text-base font-semibold">Ringkasan Status Kamar</h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard testid="stat-tersedia" label="Tersedia" value={rs.counts.tersedia || 0} icon={Home} accent="#047857" />
          <StatCard testid="stat-dipesan" label="Dipesan (DP)" value={rs.counts.dipesan || 0} icon={CircleDot} accent="#c2410c" />
          <StatCard testid="stat-menunggu" label="Menunggu Masuk" value={rs.counts.menunggu_masuk || 0} icon={Clock} accent="#0369a1" />
          <StatCard testid="stat-terisi" label="Terisi" value={rs.counts.terisi || 0} icon={DoorOpen} accent="#334155" />
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-3 text-sm">
          <Wrench className="h-4 w-4 text-[#a16207]" />
          <span className="font-medium">{rs.being_prepared}</span>
          <span className="text-muted-foreground">
            kamar sedang disiapkan (hitungan terpisah, dapat beririsan dengan status penyewaan — tidak dijumlahkan sebagai kategori kamar).
          </span>
        </div>
      </div>

      {/* Room occupancy */}
      {occ?.snapshot?.length > 0 && (
        <SectionCard
          title="Okupansi Kamar (Kos)"
          description="Perbandingan kamar terisi vs kosong per proyek, dan tren tingkat okupansi 6 bulan terakhir."
          testid="section-occupancy"
        >
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium">Status Saat Ini</span>
                <span className="text-xs text-muted-foreground" data-testid="occupancy-total">
                  Okupansi {occ.snapshot_total.rate}% · {occ.snapshot_total.occupied}/{occ.snapshot_total.total} kamar
                </span>
              </div>
              <div className="h-64 w-full" data-testid="occupancy-snapshot-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={occ.snapshot} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f5" vertical={false} />
                    <XAxis dataKey="project_name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={32} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v, n) => [`${v} kamar`, n]} />
                    <Legend />
                    <Bar dataKey="occupied" name="Terisi" stackId="a" fill="#334155" />
                    <Bar dataKey="reserved" name="Proses (DP/Menunggu)" stackId="a" fill="#c2410c" />
                    <Bar dataKey="available" name="Kosong" stackId="a" fill="#047857" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium">Tren Okupansi (%)</span>
                <span className="text-xs text-muted-foreground">6 bulan terakhir</span>
              </div>
              {(occ.trend?.data || []).some((d) => (occ.trend?.series || []).some((s) => (d[s.id] || 0) > 0)) ? (
                <div className="h-64 w-full" data-testid="occupancy-trend-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={occ.trend.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f5" vertical={false} />
                      <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} width={40} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(v, n) => [`${v}%`, n]} labelFormatter={monthLabel} />
                      <Legend />
                      {occ.trend.series.map((s, i) => (
                        <Line key={s.id} type="monotone" dataKey={s.id} name={s.name} stroke={OCC_COLORS[i % OCC_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState title="Belum ada data tren" description="Tren muncul setelah ada sewa aktif." icon={TrendingUp} />
              )}
            </div>
          </div>
        </SectionCard>
      )}

      {/* Available rooms */}
      {data?.available_rooms?.length > 0 && (
        <SectionCard
          title={`Kamar Kosong / Tersedia (${data.available_rooms.length})`}
          description="Kamar siap disewakan — langsung buat booking atau Langsung Sewa."
          testid="section-available-rooms"
          actions={<Button variant="outline" size="sm" onClick={() => navigate("/booking-sewa")} data-testid="available-rooms-go">Buka Booking & Sewa <ArrowRight className="ml-1 h-3 w-3" /></Button>}
        >
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {data.available_rooms.map((r) => (
              <div key={r.id} data-testid={`available-room-${r.id}`} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                <div>
                  <div className="font-medium">Kamar {r.number}</div>
                  <div className="text-xs text-muted-foreground">{r.project_name} · {r.house_name}</div>
                </div>
                <DoorOpen className="h-4 w-4 shrink-0 text-[#047857]" />
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Finance summary */}
      <div>
        <h2 className="mb-1 text-base font-semibold">Ringkasan Keuangan Bulan Ini</h2>
        <p className="mb-3 text-xs text-muted-foreground">
          Diakui berdasarkan tanggal pelunasan — {formatTanggal(data?.finance_period?.from)} s/d {formatTanggal(data?.finance_period?.to)}.
        </p>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard testid="fin-investor" label="Bagian Investor (80%)" value={<Money value={fin.investor} />} icon={TrendingUp} accent="#047857" />
          <StatCard testid="fin-care" label="Dana Perawatan (20%)" value={<Money value={fin.care} />} icon={Wrench} accent="#0369a1" />
          <StatCard testid="fin-ipl" label="Pendapatan IPL" value={<Money value={fin.ipl} />} icon={Wallet} accent="#7e22ce" />
          <StatCard testid="fin-dp" label="DP Hangus" value={<Money value={fin.dp_forfeit} />} icon={PiggyBank} accent="#dd6030" />
          <StatCard testid="fin-deposit-held" label="Deposit Ditahan" value={<Money value={data.deposit_held?.amount || 0} />} icon={ShieldCheck} accent="#0369a1" hint={`${data.deposit_held?.count || 0} sewa · belum dikembalikan`} />
        </div>
      </div>

      {data?.guesthouse && (
        <SectionCard
          title="Guest House — Pendapatan Bulan Ini"
          description="Pendapatan pengelola dari sewa harian, mingguan & bulanan guest house (terpisah dari investor)."
          testid="dashboard-guesthouse"
          actions={<Button variant="outline" size="sm" onClick={() => navigate("/guest-house")} data-testid="dashboard-gh-go">Buka Guest House <ArrowRight className="ml-1 h-3 w-3" /></Button>}
        >
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
            <StatCard testid="dash-gh-total" label="Total Pendapatan" value={<Money value={data.guesthouse.income} />} icon={BedDouble} accent="#047857" />
            <StatCard testid="dash-gh-daily" label="Sewa Harian" value={<Money value={data.guesthouse.daily} />} icon={TrendingUp} accent="#dd6030" />
            <StatCard testid="dash-gh-weekly" label="Sewa Mingguan" value={<Money value={data.guesthouse.weekly} />} icon={TrendingUp} accent="#b45309" />
            <StatCard testid="dash-gh-monthly" label="Sewa Bulanan" value={<Money value={data.guesthouse.monthly} />} icon={CalendarCheck} accent="#0369a1" />
            <StatCard testid="dash-gh-ipl" label="IPL Guest House" value={<Money value={data.guesthouse.ipl} />} icon={Wallet} accent="#7e22ce" />
          </div>
        </SectionCard>
      )}

      {/* Finance trend */}
      <SectionCard title="Tren Keuangan" description="Bagian investor, dana perawatan, dan IPL yang diakui per bulan (6 bulan terakhir)." testid="section-finance-trend">
        {trend.filter((t) => t.investor || t.care || t.ipl).length === 0 ? (
          <EmptyState title="Belum ada data tren" description="Data muncul setelah ada pelunasan yang diakui." icon={TrendingUp} />
        ) : (
          <div className="h-72 w-full" data-testid="finance-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gInv" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#047857" stopOpacity={0.3} /><stop offset="95%" stopColor="#047857" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gCare" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0369a1" stopOpacity={0.3} /><stop offset="95%" stopColor="#0369a1" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gIpl" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#7e22ce" stopOpacity={0.3} /><stop offset="95%" stopColor="#7e22ce" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f5" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={compactRp} tick={{ fontSize: 12 }} width={48} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v, n) => [formatRupiah(v), SERIES[n] || n]} labelFormatter={monthLabel} />
                <Legend formatter={(v) => SERIES[v] || v} />
                <Area type="monotone" dataKey="investor" stroke="#047857" fill="url(#gInv)" strokeWidth={2} />
                <Area type="monotone" dataKey="care" stroke="#0369a1" fill="url(#gCare)" strokeWidth={2} />
                <Area type="monotone" dataKey="ipl" stroke="#7e22ce" fill="url(#gIpl)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </SectionCard>

      {/* Recent activity */}
      <SectionCard title="Aktivitas Terbaru" testid="section-activity">
        {data?.recent_activity?.length ? (
          <div className="divide-y divide-border">
            {data.recent_activity.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-2.5 text-sm">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium capitalize">{a.action}</span>
                  <span className="text-muted-foreground">{a.object_type}</span>
                  {a.actor_email && <span className="text-xs text-muted-foreground">· {a.actor_email}</span>}
                </div>
                <span className="text-xs text-muted-foreground">{formatTanggalWaktu(a.created_at)}</span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState title="Belum ada aktivitas" icon={Activity} />
        )}
      </SectionCard>
    </div>
  );
}
